import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { WebSearchSettings, AgentLink } from "@/types/webSearchSettings";
import { generateWebSearchInstructions } from "@/utils/webSearchUtils";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import {
  Globe,
  Building,
  Link as LinkIcon,
  Info,
  ExternalLink,
  Plus,
  Edit,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNavigate } from "react-router-dom";
import LinkEditorModal from "./LinkEditorModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface WebSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialSettings: WebSearchSettings;
  onSave: (settings: WebSearchSettings) => Promise<boolean>;
}

const WebSearchModal: React.FC<WebSearchModalProps> = ({
  isOpen,
  onClose,
  initialSettings,
  onSave,
}) => {
  const { organization } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<WebSearchSettings>(initialSettings);
  const [isSaving, setIsSaving] = useState(false);
  const [organizationWebsite, setOrganizationWebsite] = useState<string | null>(null);
  
  // State for link editor modal
  const [isLinkEditorOpen, setIsLinkEditorOpen] = useState(false);
  const [currentEditingLink, setCurrentEditingLink] = useState<AgentLink | null>(null);
  
  // State for link deletion confirmation
  const [linkToDelete, setLinkToDelete] = useState<AgentLink | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  // Get organization website from database if not in context
  useEffect(() => {
    const fetchOrganizationDetails = async () => {
      if (!organization?.id) return;
      
      try {
        // Always fetch the latest organization row so that we avoid stale cache issues
        const { data, error } = await supabase
          .from("organizations")
          .select("website, contact_info")
          .eq("id", organization.id)
          .single();

        if (error) {
          console.error("Error fetching organization website (Supabase):", error);
        }

        // Supabase may return contact_info as a JSON object (or string). Safely extract website
        let contactWebsite: string | null = null;
        if (data?.contact_info) {
          try {
            const contactObj = typeof data.contact_info === 'string' ? JSON.parse(data.contact_info) : data.contact_info;
            if (contactObj && typeof contactObj.website === 'string') {
              contactWebsite = contactObj.website;
            }
          } catch (jsonErr) {
            console.error('Error parsing contact_info JSON:', jsonErr);
          }
        }

        const freshWebsite: string | null = data?.website ?? null;

        // Prefer freshly fetched website, fall back to context value
        const finalWebsite = freshWebsite 
          || organization.website 
          || contactWebsite 
          || (organization.contact_info && (organization.contact_info as any).website) 
          || null;
        console.log("Resolved organization website:", finalWebsite);
        setOrganizationWebsite(finalWebsite);
      } catch (err) {
        console.error("Unexpected error fetching organization website:", err);
      }
    };
    
    if (isOpen) {
      fetchOrganizationDetails();
    }
  }, [organization?.id, organization?.website, isOpen]);
  
  // Reset settings when the modal is opened
  useEffect(() => {
    if (isOpen) {
      setSettings(initialSettings);
    }
  }, [isOpen, initialSettings]);
  
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const success = await onSave(settings);
      if (success) {
        onClose();
      } else {
        toast.error("Failed to save web search settings");
      }
    } catch (error) {
      console.error("Error saving web search settings:", error);
      toast.error("An error occurred while saving settings");
    } finally {
      setIsSaving(false);
    }
  };
  
  // Link management functions
  const handleAddLink = () => {
    setCurrentEditingLink(null); // null means we're adding a new link
    setIsLinkEditorOpen(true);
  };
  
  const handleEditLink = (link: AgentLink) => {
    setCurrentEditingLink(link); // Set the link to edit
    setIsLinkEditorOpen(true);
  };
  
  const handleSaveLink = (savedLink: AgentLink) => {
    setSettings(prev => {
      // Ensure we have an array to work with even if prev.links is undefined
      const currentLinks = [...(prev.links || [])];
      
      // Check if we're editing an existing link or adding a new one
      if (currentEditingLink) {
        // Editing - replace the edited link
        return {
          ...prev,
          links: currentLinks.map(link => 
            link.id === savedLink.id ? savedLink : link
          )
        };
      } else {
        // Adding - append the new link
        return {
          ...prev,
          links: [...currentLinks, savedLink]
        };
      }
    });
    
    toast.success(currentEditingLink ? "Link updated" : "Link added");
  };
  
  const openDeleteConfirmation = (link: AgentLink) => {
    setLinkToDelete(link);
    setIsDeleteDialogOpen(true);
  };
  
  const confirmDeleteLink = () => {
    if (!linkToDelete) return;
    
    setSettings(prev => ({
      ...prev,
      links: (prev.links || []).filter(link => link.id !== linkToDelete.id)
    }));
    
    setIsDeleteDialogOpen(false);
    setLinkToDelete(null);
    toast.success("Link removed");
  };
  
  // Check if website is available
  const hasWebsite = !!organizationWebsite;
  
  // Generate preview instructions
  const instructionsPreview = generateWebSearchInstructions(
    settings,
    organizationWebsite
  );
  
  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-[600px] h-[90vh] max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-xl">Configure Web Search</DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="flex-1 pr-4 -mr-4 min-h-0">
            <div className="space-y-6 py-2">
              {/* Google Web Search Toggle */}
              <div className="flex flex-col space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-blue-100 rounded-md text-blue-600">
                    <Globe className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="google-search" className="text-base font-medium">
                        Google Web Search
                      </Label>
                      <Switch
                        id="google-search"
                        checked={settings.google_search}
                        onCheckedChange={(checked) => 
                          setSettings(prev => ({ ...prev, google_search: checked }))
                        }
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Allow your agent to search the entire web for information
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Company Website Toggle */}
              <div className="flex flex-col space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-emerald-100 rounded-md text-emerald-600">
                    <Building className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="company-website" className="text-base font-medium">
                        Search Company Website
                      </Label>
                      <Switch
                        id="company-website"
                        checked={settings.company_website}
                        onCheckedChange={(checked) => 
                          setSettings(prev => ({ ...prev, company_website: checked }))
                        }
                        disabled={!hasWebsite}
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Allow your agent to search your company website
                      {!hasWebsite && (
                        <span className="text-amber-600 block mt-1">
                          Set your website URL in Organization Settings first
                        </span>
                      )}
                    </p>
                    {hasWebsite && (
                      <div className="mt-2">
                        <Badge variant="outline" className="text-xs bg-slate-50">
                          {organizationWebsite}
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Links Management */}
              <div className="flex flex-col space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-purple-100 rounded-md text-purple-600">
                    <LinkIcon className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-medium">
                        Website Links
                      </Label>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="flex items-center gap-1 text-xs"
                        onClick={handleAddLink}
                      >
                        <Plus className="h-3 w-3" />
                        Add Link
                      </Button>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Manage specific websites that your agent can access
                    </p>
                    
                    {settings.links && settings.links.length > 0 ? (
                      <div className="mt-3 space-y-3">
                        {settings.links.map((link) => (
                          <div key={link.id} className="p-3 border rounded-md">
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-medium">{link.title}</h4>
                                <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                                  {link.url}
                                </a>
                              </div>
                              <div className="flex space-x-1">
                                <Button 
                                  size="icon" 
                                  variant="ghost" 
                                  onClick={() => handleEditLink(link)} 
                                  className="h-6 w-6"
                                >
                                  <Edit className="h-3 w-3" />
                                  <span className="sr-only">Edit</span>
                                </Button>
                                <Button 
                                  size="icon" 
                                  variant="ghost" 
                                  onClick={() => openDeleteConfirmation(link)} 
                                  className="h-6 w-6 text-red-500 hover:text-red-600"
                                >
                                  <Trash2 className="h-3 w-3" />
                                  <span className="sr-only">Delete</span>
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="py-4 text-center text-gray-500 border border-dashed rounded-md mt-2">
                        <p>No links added yet</p>
                        <p className="text-xs mt-1">
                          Click "Add Link" to add specific websites for your agent to access
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Web Search Instructions */}
              <div className="flex flex-col space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-orange-100 rounded-md text-orange-600">
                    <Info className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <Label htmlFor="web-search-instructions" className="text-base font-medium">
                      Web Search Instructions
                    </Label>
                    <p className="text-sm text-gray-500 mt-1 mb-3">
                      Provide custom instructions for how your agent should use the web search tool (optional)
                    </p>
                    <Textarea
                      id="web-search-instructions"
                      placeholder="e.g., Focus on recent news, prioritize official sources, check multiple sources for accuracy..."
                      value={settings.web_search_instructions || ''}
                      onChange={(e) => 
                        setSettings(prev => ({ ...prev, web_search_instructions: e.target.value }))
                      }
                      rows={3}
                      className="resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* Instructions Preview */}
              {instructionsPreview && (
                <div className="bg-slate-50 border rounded-md p-4">
                  <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    Preview: Instructions sent to AI
                  </h4>
                  <pre className="text-xs text-gray-600 whitespace-pre-wrap font-mono bg-white p-3 rounded border">
                    {instructionsPreview}
                  </pre>
                </div>
              )}
            </div>
          </ScrollArea>
          
          <DialogFooter className="flex-shrink-0 mt-6">
            <Button 
              variant="outline" 
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Settings"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Link Editor Modal */}
      <LinkEditorModal
        isOpen={isLinkEditorOpen}
        onClose={() => setIsLinkEditorOpen(false)}
        onSaveLink={handleSaveLink}
        initialLinkData={currentEditingLink}
      />
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the link "{linkToDelete?.title}" from this agent's web search settings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteLink} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default WebSearchModal; 